import { useEffect, useState } from 'react';
import { api } from '../lib/api';

const MR_CLR: Record<string, [string, string]> = {
  likely: ['#fee2e2', '#b91c1c'], possible: ['#fef3c7', '#b45309'], none: ['#dcfce7', '#047857'],
};
function mrBadge(m: string) {
  const [bg, fg] = MR_CLR[m] || MR_CLR.none;
  return <span style={{ background: bg, color: fg, fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 9, textTransform: 'uppercase' }}>{m === 'none' ? 'space OK' : m + ' make-ready'}</span>;
}
const ATT: Record<string, string> = {
  power_primary: '⚡ primary', power_secondary: '⚡ secondary', transformer: '🔌 transformer',
  comms: '🔗 comms', streetlight: '💡 light', guy_wire: '⌇ guy',
};
const IMG = (pano: string, heading: number) => `/api/bead/streetview-image?pano=${encodeURIComponent(pano)}&heading=${heading}`;
const svLink = (pano: string, heading: number) => `https://www.google.com/maps/@?api=1&map_action=pano&pano=${pano}&heading=${heading}`;

export default function StreetViewPoles({ segmentId, onClose }: { segmentId: string; onClose: () => void }) {
  const [data, setData] = useState<any>(null);
  const [msg, setMsg] = useState('Loading…');
  const [zoom, setZoom] = useState<{ pano: string; heading: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get(`/bead/segments/${segmentId}/streetview-poles`);
        if (cancelled) return;
        setData(d);
        setMsg(d?.results ? '' : 'No pole scan near this span yet — run "🛰 Scan all poles" on the page, then reopen.');
      } catch (e: any) { if (!cancelled) setMsg('Failed: ' + (e?.message || String(e))); }
    })();
    return () => { cancelled = true; };
  }, [segmentId]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '3vh 2vw' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 'min(980px,96vw)', maxHeight: '94vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '12px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div><b>🛰 Street View pole scan</b>{data?.results && <span style={{ color: '#64748b', fontSize: 13 }}> · {data.pole_count} poles · {data.mr_likely} likely make-ready · {data.panos} panoramas near this span</span>}</div>
            <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>AI screening from Google Street View — for bid pricing / prioritization, not engineering-grade clearance. {msg}</div>
          </div>
          <button onClick={onClose} style={{ background: '#4338ca', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Close</button>
        </div>
        <div style={{ overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!data && <div style={{ color: '#64748b', fontSize: 13, padding: 30, textAlign: 'center' }}>{msg}</div>}
          {data && !data.results && <div style={{ color: '#64748b', fontSize: 13, padding: 24, textAlign: 'center' }}>{msg}</div>}
          {(data?.results || []).map((r: any, i: number) => (
            <div key={i} style={{ display: 'flex', gap: 14, borderBottom: '1px solid #f1f5f9', paddingBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flexShrink: 0 }}>
                <img src={IMG(r.pano_id, r.heading)} alt="" loading="lazy" onClick={() => setZoom({ pano: r.pano_id, heading: r.heading })}
                  style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', display: 'block', background: '#0f172a' }} />
                <a href={svLink(r.pano_id, r.heading)} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#0369a1', display: 'inline-block', marginTop: 4 }}>↗ open live Street View (pan / zoom)</a>
              </div>
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>Pano {i + 1} · {r.date} · {(r.poles || []).length} pole(s) · {r.model === 'opus' ? '🔍 Opus-verified' : 'Haiku'} · <span style={{ color: '#94a3b8' }}>click image to enlarge</span></div>
                {(r.poles || []).length === 0 && <div style={{ fontSize: 12, color: '#94a3b8' }}>No pole detected.</div>}
                {(r.poles || []).map((p: any, j: number) => (
                  <div key={j} style={{ padding: '7px 0', borderTop: j ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{p.pole_type} · {p.side}</span>{mrBadge(p.make_ready)}
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', margin: '4px 0' }}>
                      {(p.attachments || []).map((a: string) => <span key={a} style={{ background: '#eef2ff', color: '#3730a3', fontSize: 10, padding: '1px 6px', borderRadius: 8 }}>{ATT[a] || a}</span>)}
                    </div>
                    <div style={{ fontSize: 11, color: '#475569' }}>{p.reason}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {zoom && (
        <div onClick={(e) => { e.stopPropagation(); setZoom(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 1100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, cursor: 'zoom-out' }}>
          <img src={IMG(zoom.pano, zoom.heading)} alt="" style={{ maxWidth: '94vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 8 }} />
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <a href={svLink(zoom.pano, zoom.heading)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#7dd3fc', fontSize: 14, fontWeight: 700 }}>↗ Open live Street View (pan / zoom the real pano)</a>
            <span style={{ color: '#94a3b8', fontSize: 13 }}>click anywhere to close</span>
          </div>
        </div>
      )}
    </div>
  );
}
